import type { ReactNode } from 'react';

import { HeaderCardList, HeaderCardRow } from '../../../edge/dashboard/_components/HeaderChrome';
import type { SectorAllocation } from './portfolioActionUtils';

export const HEADER_ALLOCATION_PANEL_CLASSNAME =
  'relative mt-2 min-h-[96px] overflow-hidden rounded-lg border border-bzgray-800 bg-[#122135] p-4 sm:mt-3 sm:p-5';

export const PREVIEW_ALLOCATIONS: SectorAllocation[] = [
  { color: '#3b6fd4', label: 'Technology', value: 34 },
  { color: '#2f9f78', label: 'Healthcare', value: 22 },
  { color: '#d4a017', label: 'Financials', value: 18 },
  { color: '#7c6bc4', label: 'Consumer', value: 15 },
  { color: '#7a8699', label: 'Energy', value: 11 },
];

const allocationUnlockControlClassName =
  'inline-flex min-h-9 items-center gap-2 rounded-md border border-blue-500/25 bg-blue-500/25 px-4 py-2 text-sm font-bold text-white backdrop-blur-sm';

export const HeaderSkeletonBone = ({ className }: { className: string }) => (
  <span aria-hidden className={`animate-pulse bg-white/10 ${className}`} />
);

export const HeaderCardBody = ({ children }: { children: ReactNode }) => (
  <div className="flex h-full flex-col justify-center px-4 pb-2">{children}</div>
);

export const HEADER_CHIP_ROW_CLASSNAME = 'mt-2 flex h-8 min-h-8 flex-nowrap items-center gap-2 overflow-hidden';

export const HeaderChip = ({ children }: { children: ReactNode }) => (
  <span className="inline-flex h-8 min-h-8 shrink-0 items-center whitespace-nowrap rounded-md border border-white/20 bg-bzgray-800 px-3 text-xs font-semibold leading-none text-bzgray-300">
    {children}
  </span>
);

export const HeaderChipRowSkeleton = () => (
  <>
    <HeaderSkeletonBone className="inline-flex h-8 w-[7.25rem] shrink-0 rounded-md" />
    <HeaderSkeletonBone className="inline-flex h-8 w-[6.25rem] shrink-0 rounded-md" />
  </>
);

export const HeaderCardMetricSkeleton = () => (
  <HeaderCardBody>
    <HeaderSkeletonBone className="block h-9 w-32 rounded" />
    <span className="mt-2.5 flex flex-wrap items-center gap-1.5">
      <HeaderSkeletonBone className="inline-flex h-5 w-24 rounded-full" />
      <HeaderSkeletonBone className="inline-flex h-5 w-28 rounded-full" />
    </span>
    <HeaderSkeletonBone className="mt-2 block h-5 w-36 rounded" />
  </HeaderCardBody>
);

export const HeaderCardTodaySkeleton = () => (
  <div className="flex h-full flex-col justify-center gap-2.5 px-4 py-3">
    <div className="flex min-h-0 items-start justify-between gap-3">
      <span className="min-w-0">
        <span className="flex items-center gap-3">
          <HeaderSkeletonBone className="size-11 shrink-0 rounded-lg" />
          <HeaderSkeletonBone className="block h-9 w-28 rounded" />
        </span>
        <HeaderSkeletonBone className="mt-2 block h-5 w-44 rounded" />
      </span>
      <span className="flex shrink-0 flex-col items-end">
        <HeaderSkeletonBone className="block h-3 w-28 rounded" />
        <HeaderSkeletonBone className="mt-1 block h-3.5 w-10 rounded" />
        <HeaderSkeletonBone className="mt-1 block h-3.5 w-12 rounded" />
      </span>
    </div>
    <span className="flex w-full flex-col gap-1.5">
      <HeaderSkeletonBone className="block h-2.5 w-full rounded-full" />
      <span className="flex gap-3">
        <HeaderSkeletonBone className="block h-3 w-20 rounded" />
        <HeaderSkeletonBone className="block h-3 w-20 rounded" />
      </span>
    </span>
  </div>
);

export const HeaderCardEmptySkeleton = () => (
  <div className="flex h-full flex-col items-center justify-center gap-2 px-4 pb-2 text-center" aria-hidden>
    <HeaderSkeletonBone className="block size-9 rounded-full" />
    <HeaderSkeletonBone className="block h-4 w-36 rounded" />
    <HeaderSkeletonBone className="block h-5 w-48 rounded" />
  </div>
);

export const HeaderCardMoversSkeleton = () => (
  <HeaderCardList>
    {[0, 1, 2].map(rowIndex => (
      <HeaderCardRow key={rowIndex}>
        <span className="flex min-w-0 items-center gap-2">
          <HeaderSkeletonBone className="size-5 shrink-0 rounded-[5px]" />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <HeaderSkeletonBone className="h-3.5 w-14 rounded" />
            <HeaderSkeletonBone className="h-3 w-24 rounded" />
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <HeaderSkeletonBone className="h-4 w-12 rounded" />
          <HeaderSkeletonBone className="h-5 w-14 rounded-full" />
        </span>
      </HeaderCardRow>
    ))}
  </HeaderCardList>
);

export const AllocationLoadingBody = () => (
  <div aria-hidden>
    <HeaderSkeletonBone className="block h-3 w-20 rounded" />
    <HeaderSkeletonBone className="mt-3 block h-3 w-full rounded-full" />
    <div className="mt-3 flex h-4 flex-wrap items-center gap-x-4 gap-y-1.5 overflow-hidden">
      {[0, 1, 2, 3, 4].map(legendIndex => (
        <span className="inline-flex shrink-0 items-center gap-2" key={legendIndex}>
          <HeaderSkeletonBone className="size-2 rounded-sm" />
          <HeaderSkeletonBone className="h-3 w-16 rounded" />
        </span>
      ))}
    </div>
  </div>
);

export const AllocationBarVisual = ({
  allocations,
  isPreview,
}: {
  allocations: SectorAllocation[];
  isPreview: boolean;
}) => {
  const displayTotal = allocations.reduce((total, allocation) => total + allocation.value, 0);

  return (
    <div
      className={isPreview ? 'pointer-events-none select-none' : undefined}
      style={isPreview ? { filter: 'blur(2.5px)', opacity: 0.4 } : undefined}
      aria-hidden={isPreview}
    >
      <p className="flex h-3 items-center text-xs font-bold uppercase leading-none tracking-wide text-bzgray-300">
        Allocation
      </p>
      <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-bzgray-800">
        {allocations.map((allocation, index) => {
          const pct = (allocation.value / displayTotal) * 100;
          return (
            <span
              key={`${allocation.label}-${index}`}
              title={isPreview ? undefined : `${allocation.label} ${pct.toFixed(1)}%`}
              style={{ backgroundColor: allocation.color, width: `${pct}%` }}
            />
          );
        })}
      </div>
      <div className="mt-3 flex h-4 flex-wrap items-center gap-x-4 gap-y-1.5 overflow-hidden">
        {allocations.map((allocation, index) => {
          const pct = (allocation.value / displayTotal) * 100;
          return (
            <span
              key={`${allocation.label}-legend-${index}`}
              className="inline-flex shrink-0 items-center gap-2 text-xs leading-none text-bzgray-300"
            >
              <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: allocation.color }} />
              <span>
                {allocation.label} <span className="font-semibold tabular-nums text-white">{pct.toFixed(0)}%</span>
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export const AllocationUnlockOverlay = ({ children }: { children: ReactNode }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0b1524]/55 px-4 text-center">
    {children}
  </div>
);

export const AllocationUnlockControl = ({
  onClick,
}: {
  onClick?: () => void;
}) => {
  if (onClick) {
    return (
      <button
        type="button"
        className={`${allocationUnlockControlClassName} transition-colors hover:border-blue-500/40 hover:bg-blue-500/50 hover:text-blue-300`}
        onClick={onClick}
      >
        Link brokerage to unlock
      </button>
    );
  }

  return <span className={allocationUnlockControlClassName}>Link brokerage to unlock</span>;
};

export const AllocationPreviewBody = ({ onUnlock }: { onUnlock?: () => void }) => (
  <>
    <AllocationBarVisual allocations={PREVIEW_ALLOCATIONS} isPreview />
    <AllocationUnlockOverlay>
      <AllocationUnlockControl onClick={onUnlock} />
    </AllocationUnlockOverlay>
  </>
);
